// Build version: 2026-02-10-v1 - P1 Error Handling
import { StrictMode, forwardRef } from 'react';
import axios from 'axios';
import { injectAuthHeaders } from './services/authHeaders';

// FE-1c (2026-06-12): dozens of pages call the DEFAULT axios instance directly
// (`import axios from 'axios'; axios.get('/api/v1/...')`), bypassing the shared
// clients — those requests carried no Authorization/X-Tenant-ID and 400'd with
// missing_tenant. Install the same header injection globally for relative
// (same-origin API) URLs. External absolute URLs are left untouched.
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.startsWith('/')) {
    injectAuthHeaders(config.headers);
  }
  return config;
});
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline, Alert, AlertColor } from '@mui/material';
import { SnackbarProvider, CustomContentProps, useSnackbar, closeSnackbar } from 'notistack';
import { store } from './app/store';
import { queryClient } from './app/queryClient';
import { useAppSelector } from './app/hooks';
import { lightTheme, darkTheme } from './theme/muiTheme';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import App from './App';
import './index.css';
import './theme/qvars.css';

const SuccessSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => (
    <Alert
      ref={ref}
      severity="success"
      variant="filled"
      onClose={() => closeSnackbar(id)}
      sx={{
        minWidth: 288,
        boxShadow: 3,
        '& .MuiAlert-message': { color: '#fff' },
        '& .MuiAlert-icon': { color: '#fff' },
        '& .MuiAlert-action': { color: '#fff' },
      }}
    >
      <span style={{ color: '#fff' }}>{String(message)}</span>
    </Alert>
  )
);
SuccessSnackbar.displayName = 'SuccessSnackbar';

const ErrorSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => (
    <Alert
      ref={ref}
      severity="error"
      variant="filled"
      onClose={() => closeSnackbar(id)}
      sx={{
        minWidth: 288,
        boxShadow: 3,
        '& .MuiAlert-message': { color: '#fff' },
        '& .MuiAlert-icon': { color: '#fff' },
        '& .MuiAlert-action': { color: '#fff' },
      }}
    >
      <span style={{ color: '#fff' }}>{String(message)}</span>
    </Alert>
  )
);
ErrorSnackbar.displayName = 'ErrorSnackbar';

const WarningSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => (
    <Alert
      ref={ref}
      severity="warning"
      variant="filled"
      onClose={() => closeSnackbar(id)}
      sx={{
        minWidth: 288,
        boxShadow: 3,
        '& .MuiAlert-message': { color: '#fff' },
        '& .MuiAlert-icon': { color: '#fff' },
        '& .MuiAlert-action': { color: '#fff' },
      }}
    >
      <span style={{ color: '#fff' }}>{String(message)}</span>
    </Alert>
  )
);
WarningSnackbar.displayName = 'WarningSnackbar';

const InfoSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => (
    <Alert
      ref={ref}
      severity="info"
      variant="filled"
      onClose={() => closeSnackbar(id)}
      sx={{
        minWidth: 288,
        boxShadow: 3,
        '& .MuiAlert-message': { color: '#fff' },
        '& .MuiAlert-icon': { color: '#fff' },
        '& .MuiAlert-action': { color: '#fff' },
      }}
    >
      <span style={{ color: '#fff' }}>{String(message)}</span>
    </Alert>
  )
);
InfoSnackbar.displayName = 'InfoSnackbar';

const DefaultSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message }, ref) => (
    <Alert
      ref={ref}
      severity="info"
      variant="filled"
      onClose={() => closeSnackbar(id)}
      sx={{
        minWidth: 288,
        boxShadow: 3,
        bgcolor: '#323232',
        '& .MuiAlert-message': { color: '#fff' },
        '& .MuiAlert-icon': { color: '#fff' },
        '& .MuiAlert-action': { color: '#fff' },
      }}
    >
      <span style={{ color: '#fff' }}>{String(message)}</span>
    </Alert>
  )
);
DefaultSnackbar.displayName = 'DefaultSnackbar';

function ThemedApp() {
  const { mode } = useAppSelector((state) => state.theme);
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  // Phase 9 var-ization: page colour literals are CSS vars (theme/qvars.css); this
  // attribute is what flips them. Set synchronously before paint (not in an effect)
  // so the first frame after a mode change is already in the right register.
  document.documentElement.dataset.qMode = mode;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        Components={{
          default: DefaultSnackbar,
          success: SuccessSnackbar,
          error: ErrorSnackbar,
          warning: WarningSnackbar,
          info: InfoSnackbar,
        }}
      >
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary boundaryName="Root">
          <ThemedApp />
        </ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  </StrictMode>
);
